import { useState } from 'react';
import {
  doc,
  writeBatch,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../services/firebase';

const OTP_LENGTH = 6;

export const useOTPVerification = () => {
  const [dispatchLoading, setDispatchLoading] = useState(false);
  const [otpError, setOtpError] = useState('');

  const functions = getFunctions();
  const sendOTPCallable = httpsCallable(functions, 'sendOTP');
  const verifyOTPCallable = httpsCallable(functions, 'verifyOTP');

  // Trigger SMS via sendOTP Cloud Function
  const sendDispatchOtp = async (jreMobile, sellerId) => {
    setDispatchLoading(true);
    setOtpError('');
    try {
      console.log('📱 Sending OTP via cloud function:', {
        mobile: jreMobile,
        sellerId: sellerId,
        template: 'JMI_SELLER_STOCK_ISSUE_JRE_OTP',
      });

      const response = await sendOTPCallable({
        templateName: 'JMI_SELLER_STOCK_ISSUE_JRE_OTP',
        mobile: jreMobile,
        params: {
          PARTY_ID: sellerId,
        },
      });

      console.log('✅ OTP Send Response:', response.data);

      // Return the full response data including otpId
      if (response.data?.success) {
        return {
          success: true,
          otpId: response.data?.otpId || null,
          message: response.data?.message || 'OTP sent successfully',
        };
      } else {
        setOtpError(response.data?.message || 'Failed to send OTP');
        return { success: false, otpId: null };
      }
    } catch (err) {
      console.error('❌ Error sending dispatch OTP:', err);
      setOtpError(err.message || 'Failed to send OTP.');
      return { success: false, otpId: null };
    } finally {
      setDispatchLoading(false);
    }
  };

  // Verify OTP via verifyOTP Cloud Function and update orders in Firestore
  const verifyDispatchOtp = async (secureDispatchModal, dispatchOtp, sellerId) => {
    console.log('🔐 verifyDispatchOtp called with:', {
      secureDispatchModal: secureDispatchModal ? 'present' : 'missing',
      dispatchOtp: dispatchOtp ? 'present' : 'missing',
      sellerId: sellerId ? 'present' : 'missing',
      otpId: secureDispatchModal?.otpId ? 'present' : 'MISSING',
    });

    if (!secureDispatchModal) {
      setOtpError('No secure dispatch session found');
      console.error('❌ secureDispatchModal is null/undefined');
      return false;
    }

    if (!secureDispatchModal.otpId) {
      setOtpError('OTP session expired. Please resend OTP.');
      console.error('❌ otpId is missing from secureDispatchModal');
      return false;
    }

    if (dispatchOtp.length !== OTP_LENGTH || !/^\d{6}$/.test(dispatchOtp)) {
      setOtpError('Please enter a valid 6-digit OTP');
      console.error('❌ Invalid OTP format:', dispatchOtp);
      return false;
    }

    if (!sellerId) {
      setOtpError('Seller ID not found');
      console.error('❌ Seller ID is missing');
      return false;
    }

    setDispatchLoading(true);
    setOtpError('');

    try {
      const { jreMobile, groupData, otpId } = secureDispatchModal;

      console.log('📱 Extracted from modal:', {
        jreMobile: jreMobile,
        otpId: otpId,
        groupData: groupData ? 'present' : 'missing',
        jreId: groupData?.jreId,
        operatorId: groupData?.operatorId,
      });

      // 1. Verify OTP with Cloud Function backend - PASS otpId
      console.log('🔄 Calling verifyOTP cloud function with:', {
        mobile: jreMobile,
        otpId: otpId,
        useCase: 'JMI_SELLER_STOCK_ISSUE_JRE_OTP',
        otp: '****' // Don't log actual OTP
      });

      const verifyResponse = await verifyOTPCallable({
        mobile: jreMobile,
        otpId: otpId,
        useCase: 'JMI_SELLER_STOCK_ISSUE_JRE_OTP',
        otp: dispatchOtp,
      });

      console.log('✅ OTP Verification Response:', verifyResponse.data);

      if (!verifyResponse.data?.success) {
        setOtpError(verifyResponse.data?.message || 'Invalid or expired OTP.');
        console.error('❌ OTP verification failed in cloud function');
        return false;
      }

      // 2. Fetch all matching assigned orders to update
      if (!sellerId || !groupData?.jreId || !groupData?.operatorId) {
        const missing = [];
        if (!sellerId) missing.push('sellerId');
        if (!groupData?.jreId) missing.push('jreId');
        if (!groupData?.operatorId) missing.push('operatorId');
        
        const errorMsg = `Missing required parameters: ${missing.join(', ')}`;
        setOtpError(errorMsg);
        console.error('❌', errorMsg);
        return false;
      }

      console.log('🔍 Querying orders with:', {
        sellerId: sellerId,
        orderStatus: 'Assigned',
        jreId: groupData.jreId,
        operatorId: groupData.operatorId,
      });

      const ordersQuery = query(
        collection(db, 'orderList'),
        where('sellerId', '==', sellerId),
        where('orderStatus', '==', 'Assigned'),
        where('jreId', '==', groupData.jreId),
        where('operatorId', '==', groupData.operatorId)
      );

      const ordersSnapshot = await getDocs(ordersQuery);

      console.log(`✅ Found ${ordersSnapshot.size} orders to update`);

      if (ordersSnapshot.empty) {
        setOtpError('No assigned orders found for this group.');
        console.error('❌ No orders found matching query');
        return false;
      }

      // 3. Batch update orders to 'PickedUp'
      const batch = writeBatch(db);

      ordersSnapshot.docs.forEach((orderDoc) => {
        const orderRef = doc(db, 'orderList', orderDoc.id);
        batch.update(orderRef, {
          orderStatus: 'PickedUp',
          pickedUpAt: new Date(),
          pickedUpBy: groupData.jreId,
          verifiedBy: sellerId,
          otpVerified: true,
          otpReference: otpId,
        });
      });

      await batch.commit();

      console.log(`✅ Successfully updated ${ordersSnapshot.size} orders to PickedUp status`);
      alert(`✅ ${ordersSnapshot.size} orders marked as Picked Up!`);
      return true;
    } catch (err) {
      console.error('❌ Error verifying OTP:', err);
      console.error('Error details:', {
        message: err.message,
        code: err.code,
        stack: err.stack,
      });
      setOtpError(err.message || 'Verification failed. Please try again.');
      return false;
    } finally {
      setDispatchLoading(false);
    }
  };

  return {
    sendDispatchOtp,
    verifyDispatchOtp,
    dispatchLoading,
    otpError,
    setOtpError,
  };
};